// ============================================================
// AgendaPsicologoPage — Citas del psicólogo por semana.
// ============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { I, UserAvatar } from 'components/ui';
import AtencionFormModal from 'components/psicologia/AtencionFormModal';
import { atencionPsicologicaService } from 'services/atencionPsicologicaService';
import {
  AtencionPsicologicaResponse,
  ESTADO_ATENCION_LABEL,
  ESTADO_ATENCION_COLOR,
  TIPO_ATENCION_LABEL,
  MOTIVO_CATEGORIA_LABEL,
} from 'types/psicologia.types';

const Spinner: React.FC<{ size?: number }> = ({ size = 6 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function inicioSemana(d: Date): Date {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0 = Lunes
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}
function fmtDia(d: Date): string {
  return d.toLocaleDateString('es-PE', { weekday: 'long', day: '2-digit', month: 'short' });
}
function fmtHora(iso: string): string {
  return iso.slice(11, 16);
}

const AgendaPsicologoPage: React.FC = () => {
  const [semanaStart, setSemanaStart] = useState<Date>(inicioSemana(new Date()));
  const [citas, setCitas] = useState<AtencionPsicologicaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AtencionPsicologicaResponse | null>(null);
  const [fechaSugerida, setFechaSugerida] = useState<string | undefined>();

  const semanaEnd = useMemo(() => addDays(semanaStart, 6), [semanaStart]);

  const cargar = async () => {
    setLoading(true); setError(null);
    try {
      const data = await atencionPsicologicaService.getAgenda(
        `${ymd(semanaStart)}T00:00:00`,
        `${ymd(semanaEnd)}T23:59:59`,
      );
      setCitas(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar la agenda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [semanaStart.getTime()]);

  // Agrupar por día
  const porDia = useMemo(() => {
    const map = new Map<string, AtencionPsicologicaResponse[]>();
    for (let i = 0; i < 7; i++) {
      const k = ymd(addDays(semanaStart, i));
      map.set(k, []);
    }
    citas.forEach(c => {
      const k = c.fechaHora.slice(0, 10);
      const arr = map.get(k);
      if (arr) arr.push(c);
    });
    map.forEach(arr => arr.sort((a, b) => a.fechaHora.localeCompare(b.fechaHora)));
    return map;
  }, [citas, semanaStart]);

  const onSaved = (a: AtencionPsicologicaResponse) => {
    setShowForm(false); setEditing(null);
    setCitas(prev => {
      const idx = prev.findIndex(c => c.idAtencionPsicologica === a.idAtencionPsicologica);
      if (idx >= 0) { const next = [...prev]; next[idx] = a; return next; }
      return [...prev, a];
    });
  };

  const eliminar = async (a: AtencionPsicologicaResponse) => {
    if (!window.confirm(`¿Eliminar la sesión del ${fmtHora(a.fechaHora)} con ${a.estudianteApellidos}?`)) return;
    try {
      await atencionPsicologicaService.eliminar(a.idAtencionPsicologica);
      setCitas(prev => prev.filter(c => c.idAtencionPsicologica !== a.idAtencionPsicologica));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al eliminar');
    }
  };

  const total = citas.length;
  const programadas = citas.filter(c => c.estado === 'PROGRAMADA').length;
  const realizadas  = citas.filter(c => c.estado === 'REALIZADA').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Agenda de psicología</h2>
          <p className="text-slate-400 text-sm mt-0.5">Sesiones programadas en la semana</p>
        </div>
        <button
          onClick={() => { setEditing(null); setFechaSugerida(undefined); setShowForm(true); }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-sm font-bold"
        >
          <I.Plus size={14} /> Nueva sesión
        </button>
      </div>

      {/* Selector de semana + KPIs */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSemanaStart(inicioSemana(addDays(semanaStart, -7)))}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Semana anterior"
          >
            <I.ChevronLeft size={18} />
          </button>
          <span className="text-sm font-bold text-slate-700 capitalize">
            {fmtDia(semanaStart)} — {fmtDia(semanaEnd)}
          </span>
          <button
            onClick={() => setSemanaStart(inicioSemana(addDays(semanaStart, 7)))}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Semana siguiente"
          >
            <I.ChevronRight size={18} />
          </button>
          <button
            onClick={() => setSemanaStart(inicioSemana(new Date()))}
            className="ml-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700"
          >
            Hoy
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">Total: {total}</span>
          <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">Programadas: {programadas}</span>
          <span className="px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">Realizadas: {realizadas}</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>
      )}

      {/* Grid de 7 días */}
      <div className="grid md:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => {
          const dia = addDays(semanaStart, i);
          const fechaKey = ymd(dia);
          const dayCitas = porDia.get(fechaKey) ?? [];
          const isHoy = ymd(new Date()) === fechaKey;
          return (
            <div key={i} className={`bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col
              ${isHoy ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-slate-100'}`}>
              <div className={`px-3 py-2 border-b text-xs font-bold uppercase tracking-wide flex items-center justify-between
                ${isHoy ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-slate-50 text-slate-500 border-slate-100'}`}>
                <span>{DIAS[i]} {dia.getDate()}</span>
                <button
                  onClick={() => { setEditing(null); setFechaSugerida(fechaKey); setShowForm(true); }}
                  className="text-[10px] px-1.5 py-0.5 rounded-md hover:bg-white/70"
                  title="Nueva sesión este día"
                >
                  <I.Plus size={10} />
                </button>
              </div>
              <div className="p-2 space-y-2 flex-1 min-h-[80px]">
                {loading && dayCitas.length === 0 && (
                  <div className="text-xs text-slate-300 text-center py-4">…</div>
                )}
                {!loading && dayCitas.length === 0 && (
                  <p className="text-xs text-slate-300 text-center py-4">Sin citas</p>
                )}
                {dayCitas.map(c => (
                  <button
                    key={c.idAtencionPsicologica}
                    onClick={() => { setEditing(c); setFechaSugerida(undefined); setShowForm(true); }}
                    className="w-full text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-2 py-1.5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-800 tabular-nums">{fmtHora(c.fechaHora)}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0 rounded-full border ${ESTADO_ATENCION_COLOR[c.estado]}`}>
                        {ESTADO_ATENCION_LABEL[c.estado]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 font-semibold mt-0.5 truncate">
                      {c.estudianteApellidos}, {c.estudianteNombres}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {TIPO_ATENCION_LABEL[c.tipo]}
                      {c.motivoCategoria && <> · {MOTIVO_CATEGORIA_LABEL[c.motivoCategoria]}</>}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lista de citas detallada (extra) */}
      {citas.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Detalle de sesiones</span>
          </div>
          <ul className="divide-y divide-slate-50">
            {citas
              .slice()
              .sort((a, b) => a.fechaHora.localeCompare(b.fechaHora))
              .map(c => (
                <li key={c.idAtencionPsicologica} className="px-5 py-3 flex items-center gap-4">
                  <UserAvatar
                    idUsuario={c.estudianteId}
                    fotoPerfil={c.estudianteFotoPerfil}
                    nombres={c.estudianteNombres}
                    apellidos={c.estudianteApellidos}
                    size={40}
                    rounded="xl"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">
                      {c.estudianteApellidos}, {c.estudianteNombres}
                    </p>
                    <p className="text-xs text-slate-500">
                      {c.fechaHora.slice(0, 10)} · {fmtHora(c.fechaHora)} · {c.duracionMin} min · {c.gradoNombre} {c.seccionNombre}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${ESTADO_ATENCION_COLOR[c.estado]}`}>
                    {ESTADO_ATENCION_LABEL[c.estado]}
                  </span>
                  <button
                    onClick={() => { setEditing(c); setShowForm(true); }}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-700 hover:bg-indigo-50"
                    title="Editar"
                  >
                    <I.Edit size={14} />
                  </button>
                  <button
                    onClick={() => eliminar(c)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Eliminar"
                  >
                    <I.Trash size={14} />
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}

      {showForm && (
        <AtencionFormModal
          initial={editing}
          fechaSugerida={fechaSugerida}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default AgendaPsicologoPage;
